import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronLeft, ChevronRight, Users, Clock } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useDutyRosterManagement } from "@/hooks/useDutyRosterManagement";
import { useQuery } from "@tanstack/react-query";
import { ShiftService } from "@/services/shiftService";
import { DutyRosterService } from "@/services/dutyRosterService";
import { useHolidaysForMonth } from "@/hooks/useQueryHooks";
import { getResidencyLabel } from '@/utils/residencyUtils';
import { useAuth } from "@/contexts/auth";
import { toast } from "sonner";

interface RosterCalendarProps {
  companyId: string;
  onOpenHolidayOverride?: (groupId: string, monthYear: string) => void;
}

interface CalendarDay {
  date: number;
  isOffDay: boolean;
  isHoliday: boolean;
  isCurrentMonth: boolean;
  isToday: boolean;
  assignedShift?: string; // shift ID assigned to this date
  shiftName?: string; // shift name for display
  shiftColor?: string; // color for this shift
  holidayName?: string; // holiday name for display
}

export function RosterCalendar({ companyId }: RosterCalendarProps) {
  useAuth();
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [selectedShiftId, setSelectedShiftId] = useState<string>("");
  const [dateAssignments, setDateAssignments] = useState<Record<number, string>>({}); // date -> shiftId
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showChangeWarning, setShowChangeWarning] = useState(false);
  const [pendingChange, setPendingChange] = useState<() => void>(() => {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const monthYear = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;

  const { groups } = useDutyRosterManagement(companyId);
  const { data: shifts } = useQuery({
    queryKey: ['shifts', companyId],
    queryFn: () => ShiftService.fetchShifts(companyId),
    enabled: !!companyId,
  });

  // Fetch holidays for the current month
  const { data: holidays } = useHolidaysForMonth(companyId, currentYear, currentMonth);

  // Filter shifts for working hours only
  const workingShifts = useMemo(() => {
    return shifts?.filter(shift => shift.in_working_hours === true) || [];
  }, [shifts]);

  // State to track off days from database
  const [dbOffDays, setDbOffDays] = useState<number[]>([]);

  // Create a map of holiday dates for quick lookup
  const holidayDates = useMemo(() => {
    if (!holidays) return new Set<number>();
    return new Set(holidays.map(holiday => {
      const date = new Date(holiday.date);
      return date.getDate();
    }));
  }, [holidays]);

  // Create a map of holiday names for display
  const holidayNames = useMemo(() => {
    if (!holidays) return new Map<number, string>();
    const map = new Map<number, string>();
    holidays.forEach(holiday => {
      const date = new Date(holiday.date);
      map.set(date.getDate(), holiday.name);
    });
    return map;
  }, [holidays]);

  // Fetch existing schedule when group or month changes
  useEffect(() => {
    const loadExistingSchedule = async () => {
      if (!selectedGroupId || !monthYear) {
        setDateAssignments({});
        setDbOffDays([]);
        return;
      }

      setIsLoadingSchedule(true);
      try {
        const schedule = await DutyRosterService.fetchGroupSchedule(selectedGroupId, monthYear);
        
        // Convert working days to date assignments
        const newDateAssignments: Record<number, string> = {};
        schedule.workingDays.forEach(({ shiftId, workingDays }) => {
          workingDays.forEach(day => {
            newDateAssignments[day] = shiftId;
          });
        });
        
        setDateAssignments(newDateAssignments);
        setDbOffDays(schedule.offDays); // Store off days from database
        setHasUnsavedChanges(false); // Reset unsaved changes flag
        
        // Show success message if schedule was loaded
        if (schedule.workingDays.length > 0 || schedule.offDays.length > 0) {
          toast.success(`Loaded existing schedule: ${schedule.workingDays.length} shifts, ${schedule.offDays.length} off days`);
        } else {
          // No schedule assigned for this month
        }
      } catch (error) {
        console.error('Error loading existing schedule:', error);
        toast.error('Failed to load existing schedule');
        setDateAssignments({});
        setDbOffDays([]);
      } finally {
        setIsLoadingSchedule(false);
      }
    };

    loadExistingSchedule();
  }, [selectedGroupId, monthYear, groups]);

  // Generate colors for shifts
  const shiftColors = useMemo(() => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 
      'bg-pink-500', 'bg-indigo-500', 'bg-yellow-500', 'bg-red-500',
      'bg-teal-500', 'bg-cyan-500', 'bg-lime-500', 'bg-amber-500'
    ];
    
    const colorMap: Record<string, string> = {};
    workingShifts.forEach((shift, index) => {
      colorMap[shift.id] = colors[index % colors.length];
    });
    return colorMap;
  }, [workingShifts]);

  const selectedGroup = groups.find(group => group.id === selectedGroupId);

  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const today = new Date();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const days: CalendarDay[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({
        date: 0,
        isOffDay: false,
        isHoliday: false,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const assignedShiftId = dateAssignments[day];
      const assignedShift = workingShifts.find(shift => shift.id === assignedShiftId);
      const isToday = today.getDate() === day && 
                     today.getMonth() === month && 
                     today.getFullYear() === year;
      
      // Determine if this day is an off day based on database data (including holidays)
      const isOffDay = !assignedShiftId && dbOffDays.includes(day);
      
      // Check if this day is a holiday
      const isHoliday = holidayDates.has(day);
      const holidayName = holidayNames.get(day);
      
      days.push({
        date: day,
        isOffDay,
        isHoliday,
        isCurrentMonth: true,
        isToday,
        assignedShift: assignedShiftId,
        shiftName: assignedShift?.name,
        shiftColor: assignedShiftId ? shiftColors[assignedShiftId] : undefined,
        holidayName
      });
    }
    
    return days;
  }, [currentDate, dateAssignments, workingShifts, shiftColors, dbOffDays, holidayDates, holidayNames]);


  const handleGroupChange = (groupId: string) => {
    if (hasUnsavedChanges) {
      setPendingChange(() => () => {
        setSelectedGroupId(groupId);
        setSelectedShiftId("");
        setDateAssignments({});
        setDbOffDays([]);
        setHasUnsavedChanges(false);
      });
      setShowChangeWarning(true);
    } else {
      setSelectedGroupId(groupId);
      setSelectedShiftId("");
      setDateAssignments({});
      setDbOffDays([]);
      setHasUnsavedChanges(false);
    }
  };

  const handleShiftChange = (shiftId: string) => {
    setSelectedShiftId(shiftId);
    // Don't reset date assignments - maintain state across shift changes
  };

  const handleDateClick = (day: number) => {
    if (!day) return;

    // Allow interaction with holiday dates - user can assign shifts or leave as off days
    
    // If no shift is selected, clear the assignment for this date (will be treated as off day)
    if (!selectedShiftId) {
      setDateAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[day];
        setHasUnsavedChanges(true);
        return newAssignments;
      });
      return;
    }
    
    setDateAssignments(prev => {
      const currentShift = prev[day];
      if (currentShift === selectedShiftId) {
        // Remove assignment if clicking the same shift (will be treated as off day)
        const newAssignments = { ...prev };
        delete newAssignments[day];
        setHasUnsavedChanges(true);
        return newAssignments;
      } else {
        // Assign new shift (even on holidays)
        setHasUnsavedChanges(true);
        return { ...prev, [day]: selectedShiftId };
      }
    });
  };

  const handlePreviousMonth = () => {
    if (hasUnsavedChanges) {
      setPendingChange(() => () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
        setDateAssignments({});
        setDbOffDays([]);
        setHasUnsavedChanges(false);
      });
      setShowChangeWarning(true);
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (hasUnsavedChanges) {
      setPendingChange(() => () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
        setDateAssignments({});
        setDbOffDays([]);
        setHasUnsavedChanges(false);
      });
      setShowChangeWarning(true);
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  };

  const handleSubmitSchedule = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedGroupId || Object.keys(dateAssignments).length === 0) {
      toast.error("Please select a group and assign at least one shift to a date");
      return;
    }

    setIsSubmitting(true);
    try {
      // Calculate off days (all days in month minus assigned days only)
      // Holiday dates are treated like regular dates - assigned = working day, unassigned = off day
      const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
      const allDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
      const assignedDays = Object.keys(dateAssignments).map(Number);
      const offDays = allDays.filter(day => 
        !assignedDays.includes(day)  // Any unassigned day (including holidays) becomes off day
      );

      // Group working days by shift (including holidays if assigned)
      const shiftGroups: Record<string, number[]> = {};
      Object.entries(dateAssignments).forEach(([day, shiftId]) => {
        const dayNum = Number(day);
        // Include ALL assigned days as working days (including holidays)
        if (!shiftGroups[shiftId]) {
          shiftGroups[shiftId] = [];
        }
        shiftGroups[shiftId].push(dayNum);
      });

      const savePromises = [];

      // Get existing shifts to handle deletions
      const existingSchedule = await DutyRosterService.fetchGroupSchedule(selectedGroupId, monthYear);
      const existingShiftIds = new Set(existingSchedule.workingDays.map(item => item.shiftId));
      const currentShiftIds = new Set(Object.keys(shiftGroups));

      // Handle shifts that were completely removed (delete them)
      const removedShiftIds = [...existingShiftIds].filter(shiftId => !currentShiftIds.has(shiftId));
      removedShiftIds.forEach(shiftId => {
        savePromises.push(
          DutyRosterService.saveWorkingDays(
            selectedGroupId,
            shiftId,
            [], // Empty array will trigger deletion
            monthYear
          )
        );
      });

      // Save each shift group separately (working days only)
      Object.entries(shiftGroups).forEach(([shiftId, workingDays]) => {
        savePromises.push(
          DutyRosterService.saveWorkingDays(
            selectedGroupId,
            shiftId,
            workingDays,
            monthYear
          )
        );
      });

      // Save off days separately (only if there are off days)
      if (offDays.length > 0) {
        savePromises.push(
          DutyRosterService.saveOffDays(
            selectedGroupId,
            offDays,
            monthYear
          )
        );
      }

      await Promise.all(savePromises);

      const totalWorkingDays = assignedDays.length;
      toast.success(`Schedule saved successfully! ${totalWorkingDays} working days across ${Object.keys(shiftGroups).length} shifts, ${offDays.length} off days`);
      
      // Reload the schedule from database instead of just resetting
      try {
        const schedule = await DutyRosterService.fetchGroupSchedule(selectedGroupId, monthYear);
        
        // Convert working days to date assignments
        const newDateAssignments: Record<number, string> = {};
        schedule.workingDays.forEach(({ shiftId, workingDays }) => {
          workingDays.forEach(day => {
            newDateAssignments[day] = shiftId;
          });
        });
        
        setDateAssignments(newDateAssignments);
        setDbOffDays(schedule.offDays);
      } catch (reloadError) {
        console.error('Error reloading schedule after save:', reloadError);
        // Fallback to reset if reload fails
        setDateAssignments({});
        setDbOffDays([]);
      }
      
      setHasUnsavedChanges(false);
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error("Failed to save schedule");
    } finally {
      setIsSubmitting(false);
    }
  };


  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-sm border-border/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Roster Calendar</h2>
                <p className="text-sm text-muted-foreground">
                  View and manage monthly duty roster schedules
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Select value={selectedGroupId} onValueChange={handleGroupChange}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(group => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.group_name} ({getResidencyLabel(group.residency_type)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedGroupId && (
                <Select value={selectedShiftId} onValueChange={handleShiftChange} disabled={isLoadingSchedule}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder={isLoadingSchedule ? "Loading schedule..." : "Select a shift"} />
                  </SelectTrigger>
                  <SelectContent>
                    {workingShifts.map(shift => (
                      <SelectItem key={shift.id} value={shift.id}>
                        {shift.name} ({shift.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Calendar */}
      <Card className="shadow-sm border-border/50">
        <CardContent className="p-0">
          {!selectedGroupId ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">Select a Group</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose a duty roster group to view its schedule
              </p>
            </div>
          ) : isLoadingSchedule ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <h3 className="mt-4 text-lg font-medium">Loading Schedule</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Fetching existing schedule for this group and month
              </p>
            </div>
          ) : (
            <div className="p-6">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <Button variant="outline" size="sm" onClick={handlePreviousMonth}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <h3 className="text-lg font-semibold">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h3>
                  <Button variant="outline" size="sm" onClick={handleNextMonth}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                {selectedGroup && (
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{selectedGroup.group_name}</span>
                    <Badge variant="secondary">
                      {getResidencyLabel(selectedGroup.residency_type)}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Day Headers */}
                {dayNames.map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                    {day}
                  </div>
                ))}
                
                {/* Calendar Days */}
                {calendarDays.map((day, index) => (
                  <div
                    key={index}
                    onClick={() => handleDateClick(day.date)}
                    className={`
                      p-2 min-h-[60px] border border-border/50 rounded-md relative
                      ${day.isCurrentMonth ? 'bg-background' : 'bg-muted/20'}
                      ${day.isToday ? 'ring-2 ring-primary' : ''}
                      ${day.isHoliday ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800' : ''}
                      ${day.isCurrentMonth ? 'cursor-pointer hover:bg-muted' : ''}
                      flex flex-col items-center justify-center
                    `}
                  >
                    {day.isCurrentMonth && (
                      <>
                        <span className={`text-sm font-medium ${day.isToday ? 'text-primary' : ''} ${day.isHoliday ? 'text-red-700 dark:text-red-300' : ''} ${day.assignedShift ? 'text-white font-bold bg-black/20 px-1 py-0.5 rounded' : ''}`}>
                          {day.date}
                        </span>
                        {day.assignedShift && (
                          <div className={`mt-1 px-2 py-1 rounded text-xs text-white ${day.shiftColor}`}>
                            {workingShifts.find(s => s.id === day.assignedShift)?.code || day.shiftName}
                          </div>
                        )}
                        {day.isHoliday && (
                          <Badge variant="destructive" className="mt-1 text-xs">
                            {day.holidayName || 'Holiday'}
                          </Badge>
                        )}
                        {day.isOffDay && !day.assignedShift && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            Off
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-background border border-border rounded"></div>
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary" className="text-xs">Off</Badge>
                  <span className="text-sm text-muted-foreground">Off Day</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="destructive" className="text-xs">Holiday</Badge>
                  <span className="text-sm text-muted-foreground">Holiday</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-primary rounded ring-2 ring-primary"></div>
                  <span className="text-sm text-muted-foreground">Today</span>
                </div>
                {/* Dynamic shift legend */}
                {workingShifts.map(shift => (
                  <div key={shift.id} className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded ${shiftColors[shift.id]}`}></div>
                    <span className="text-sm text-muted-foreground">{shift.code || shift.name}</span>
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              {hasUnsavedChanges && (
                <div className="mt-6 flex items-center justify-center">
                  <Button 
                    onClick={handleSubmitSchedule}
                    disabled={isSubmitting}
                    className="flex items-center space-x-2"
                  >
                    <Clock className="w-4 h-4" />
                    <span>{isSubmitting ? "Saving..." : `Save Changes (${Object.keys(dateAssignments).length} days assigned)`}</span>
                  </Button>
                </div>
              )}

            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Schedule Submission</AlertDialogTitle>
            <AlertDialogDescription>
              You have assigned <strong>{Object.keys(dateAssignments).length} working days</strong> across <strong>{new Set(Object.values(dateAssignments)).size} different shifts</strong> for this month.
              <br /><br />
              <strong>All unassigned dates will be considered off days</strong> for the group "{selectedGroup?.group_name}" in {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}.
              <br /><br />
              This will overwrite any existing schedule for this group and month. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? "Saving..." : "Confirm & Save Schedule"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Warning Dialog */}
      <AlertDialog open={showChangeWarning} onOpenChange={setShowChangeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved shift assignments for this month. Changing the group or month will lose these assignments.
              <br /><br />
              Are you sure you want to proceed? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowChangeWarning(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                pendingChange();
                setShowChangeWarning(false);
                setPendingChange(() => {});
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
